import { useMemo, useEffect, useState } from 'react';
import { SummaryCards } from './SummaryCards';
import { RecentActivity } from './RecentActivity';
import { Button } from '@/components/ui';
import { useUIStore, useSettingsStore, useAuthStore } from '@/stores';

export function DashboardPage() {
  const { user } = useAuthStore();
  const { members } = useSettingsStore();
  const { pageFilters, setOwnerFilter, resetAllOwnerFilters } = useUIStore();

  const dashboardOwnerFilter = pageFilters?.dashboard?.ownerFilter;

  // Default to current user on first load (if no filter set)
  // Bug #5: Also reset if owner filter references non-existent user
  useEffect(() => {
    if (user?.uid && dashboardOwnerFilter === null) {
      setOwnerFilter('dashboard', user.uid);
    } else if (dashboardOwnerFilter && dashboardOwnerFilter !== '' && members.length > 0) {
      const ownerExists = members.some(m => m.userId === dashboardOwnerFilter);
      if (!ownerExists) {
        setOwnerFilter('dashboard', user?.uid || '');
      }
    }
  }, [user?.uid, dashboardOwnerFilter, members, setOwnerFilter]);

  // Owner dropdown options - all users see all org members
  const ownerOptions = useMemo(() => {
    return [
      { value: '', label: 'All Owners' },
      ...members.map(m => ({
        value: m.userId,
        label: m.displayName || m.email || m.userId
      }))
    ];
  }, [members]);

  const handleOwnerChange = (ownerId: string) => {
    // Use empty string for "All Owners" (not null, which triggers auto-default)
    setOwnerFilter('dashboard', ownerId === '' ? '' : ownerId);
  };

  const [resetButtonText, setResetButtonText] = useState('Reset All Views');

  const handleResetAllViews = () => {
    // Convert empty string or undefined to null for other pages (they use null for "All Owners")
    // If a specific user is selected, use that user ID
    let filterValue: string | null;
    if (!dashboardOwnerFilter || dashboardOwnerFilter === '') {
      filterValue = null; // "All Owners"
    } else {
      filterValue = dashboardOwnerFilter; // Specific user
    }
    resetAllOwnerFilters(filterValue);
    setResetButtonText('Done');
    setTimeout(() => setResetButtonText('Reset All Views'), 1000);
  };

  return (
    <div className="space-y-6 pb-[50px]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          <select
            value={dashboardOwnerFilter || ''}
            onChange={(e) => handleOwnerChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
          >
            {ownerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button variant="primary" size="sm" onClick={handleResetAllViews} className="relative">
            <span className="invisible">Reset All Views</span>
            <span className="absolute inset-0 flex items-center justify-center">{resetButtonText}</span>
          </Button>
        </div>
      </div>

      <SummaryCards ownerFilter={dashboardOwnerFilter} />
      <RecentActivity ownerFilter={dashboardOwnerFilter} />
    </div>
  );
}
